import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import Fade from '@material-ui/core/Fade';
import useMediaQuery from '@material-ui/core/useMediaQuery';

// CONSTANTS
import { DisplayType } from '@/constants/watchList';

// UTILITY
import { analyticsClickedOnContent, analyticsHoverOnContent } from '@/utility/analytics';

// HOC
import Measure from '@/hoc/Measure';

// HELP
import EpisodeCardWithContent from './help/EpisodeCardWithContent';
import EpisodeCard from './help/EpisodeCard';

// STYLES
import { TooltipPopperComponent } from './styles';

const EpisodeCardWithTooltip = ({
  page,
  loading,
  isAuth,
  partner,
  data,
  fullHeight,
  disabled,
  changeContentStatus,
  onClick,
  tooltip,
  tooltipProps,
  analyticsLocation,
  swimlaneAnalytics,
}) => {
  const [open, setOpen] = useState(false);
  const matchesUpSm = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));

  function onClose() {
    setOpen(false);
  }

  function onOpen() {
    setOpen(true);
  }

  function onCardClick(...params) {
    setTimeout(() => {
      const properties = {
        ...swimlaneAnalytics,
        content_id: data.id || '',
        content_title: data.title || '',
      };

      analyticsClickedOnContent(properties);
      onClick(...params);
    }, 0);

    if (matchesUpSm) {
      onClose();
    }
  }

  const isWatchlistPage = page === 'watchlist';
  const hasTooltip = matchesUpSm && tooltip && !loading && !isWatchlistPage;

  const card = (
    <EpisodeCard
      loading={loading}
      data={data}
      fullHeight={fullHeight}
      disabled={disabled}
      onClick={onCardClick}
    />
  );

  if (!hasTooltip) return card;

  const onEntered = () => {
    const properties = {
      ...swimlaneAnalytics,
      content_id: data.id,
      content_title: data.title,
    };

    analyticsHoverOnContent(properties);
  };

  const wrapperStyle = { width: '100%', height: '100%' };

  return (
    <Measure>
      {([cardRef, { width: cardwidth, height: cardheight }]) => {
        return (
          <Measure>
            {([tooltipRef, { width: tooltipwidth, height: tooltipheight }]) => {
              return (
                <Tooltip
                  PopperComponent={TooltipPopperComponent}
                  TransitionComponent={Fade}
                  PopperProps={{
                    cardwidth,
                    cardheight,
                    tooltipwidth,
                    tooltipheight,
                    onClick: () => onCardClick(data),
                  }}
                  placement="right-start"
                  arrow={false}
                  interactive
                  enterDelay={100}
                  enterNextDelay={200}
                  open={open}
                  onClose={onClose}
                  onOpen={onOpen}
                  title={
                    <div ref={tooltipRef} style={wrapperStyle}>
                      <EpisodeCardWithContent
                        data={data}
                        page={page}
                        isAuth={isAuth}
                        changeContentStatus={changeContentStatus}
                        className="movie-card__content"
                        displayType={DisplayType.TOOLTIP}
                        partner={partner}
                        analyticsLocation={analyticsLocation}
                      />
                    </div>
                  }
                  TransitionProps={{ onEntered }}
                  {...tooltipProps}
                >
                  <div ref={cardRef} style={wrapperStyle}>
                    {card}
                  </div>
                </Tooltip>
              );
            }}
          </Measure>
        );
      }}
    </Measure>
  );
};

EpisodeCardWithTooltip.defaultProps = {
  page: '',
  tooltip: true,
  tooltipProps: {},
  data: {},
  onClick: () => {},
  fullHeight: false,
  disabled: false,
  loading: false,
  isAuth: false,
  partner: {},
  swimlaneAnalytics: {},
};

EpisodeCardWithTooltip.propTypes = {
  page: PropTypes.string,
  tooltipProps: PropTypes.object,
  tooltip: PropTypes.bool,
  data: PropTypes.object,
  isAuth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  changeContentStatus: PropTypes.func.isRequired,
  partner: PropTypes.object,
  analyticsLocation: PropTypes.string,
  swimlaneAnalytics: PropTypes.object,
};

export default React.memo(EpisodeCardWithTooltip);
